// ============================================================
// ELC Shift — Uploads / Dev Reports module (v102.0)
//
// The "Dev" admin tab — pilot-day-N triage surface for everything
// that needs admin attention:
//
//   • Feedback     — user feedback / wishlist items + admin notes
//   • Errors       — error reports grouped by message
//   • Edit history — audit log of admin edits (rota, staff, balances)
//   • PIN reveals  — audit log of "show PIN" actions (compliance)
//   • Write audit  — DB write log (rota updates, etc.)
//   • Status       — daily system health snapshot
//   • Smoke        — pilot stores' daily smoke test results
//
// All sub-tabs live in this module. The dev tab is admin-only;
// no user-facing surface depends on these functions.
//
// Public surface (all callable from admin.html inline + dynamic
// HTML attrs):
//   loadDevReports, populateDevStoreFilter, escapeDevHtml,
//   renderDevReports, renderDevRow, toggleDevRow, toggleDevStatus,
//   saveDevNotes, switchDevSubTab, loadDevWriteAudit, loadDevReveals,
//   renderDevStatus, loadDevAudit, renderDevAudit
//
// Internal calls between these (e.g. renderDevReports → renderDevRow
// → escapeDevHtml) resolve as module-local bare references and don't
// need qualifying. External callers in admin.html use
// Uploads.devReports.X form.
//
// Free globals consumed:
//   DB, showToast, ALL_STORES, formatDateShort, daysAgo, escapeHtml,
//   loadErrorReports (still inline — error-reports has its own helpers)
// ============================================================

import { adminToken as sharedAdminToken } from '../shared/admin-token.js';   // v136.12

export function escapeDevHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// v129.117 — who the close-the-loop email actually goes to. Reports filed from
// the ADMIN app carry the literal store 'Admin app' (an area manager or Head
// Office person, i.e. an `accounts` row, not a store), so the label must name the
// PERSON. Saying "tell Admin app what was done" was the giveaway that the whole
// path only ever considered stores.
function reportRecipientLabel(r) {
  const row = r || {};
  const role = String(row.user_role || '').toLowerCase();
  const isAccount = String(row.store || '').trim() === 'Admin app'
    || role === 'admin' || role === 'area_manager' || role === 'ho_staff';
  if (isAccount) return row.user_name || 'them';
  return row.store || 'the store';
}

export function populateDevStoreFilter() {
  const sel = document.getElementById('devStoreFilter');
  if (!sel) return;
  // Keep currently-selected value through the rebuild
  const currentVal = sel.value;
  const stores = Array.from(new Set(_devReports.map(r => r.store).filter(Boolean))).sort();
  sel.innerHTML = '<option value="">All stores</option>' +
    stores.map(s => '<option value="' + escapeDevHtml(s) + '">' + escapeDevHtml(s) + '</option>').join('');
  if (currentVal && stores.indexOf(currentVal) !== -1) sel.value = currentVal;
}

export function renderDevRow(r) {
  const submitted = r.submitted_at
    ? new Date(r.submitted_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
  const statusBadge = r.status === 'done'
    ? '<span style="background:#d1fae5;color:#065f46;padding:3px 10px;border-radius:6px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.3px">✓ Done</span>'
    : '<span style="background:#fef3c7;color:#92400e;padding:3px 10px;border-radius:6px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.3px">New</span>';
  const doneHint = (r.status === 'done' && r.done_at)
    ? ('<span style="font-size:11px;color:var(--sub);margin-left:6px">closed ' + new Date(r.done_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + (r.done_by ? ' by ' + escapeDevHtml(r.done_by) : '') + '</span>')
    : '';
  const expanded = _devExpanded.has(r.id);
  const caret = expanded ? '▼' : '▶';
  // v127.44: 💡 ideas share the pipeline — badge them so the triage eye can
  // split feature asks from defects at a glance.
  const kindBadge = r.kind === 'idea'
    ? '<span style="background:#ede9fe;color:#5b21b6;padding:3px 10px;border-radius:6px;font-size:11px;font-weight:700">💡 Idea</span>'
    : '';
  const summary = (r.doing || '').length > 120 ? (r.doing.slice(0, 120) + '…') : (r.doing || '—');
  // v124.16: the list embeds feedback_photos(count); PostgREST returns it as
  // an array [{count:N}]. A 📎 marker flags reports with screenshots; the
  // images themselves load on demand when the row is expanded.
  const shotCount = (Array.isArray(r.feedback_photos) && r.feedback_photos[0] && r.feedback_photos[0].count) || 0;
  const shotBadge = shotCount > 0
    ? '<span style="font-size:12px;color:var(--sub)" title="' + shotCount + ' screenshot' + (shotCount === 1 ? '' : 's') + '">📎 ' + shotCount + '</span>'
    : '';

  let detail = '';
  if (expanded) {
    const ctxBits = [];
    if (r.user_name)  ctxBits.push('<strong>User:</strong> ' + escapeDevHtml(r.user_name) + (r.user_role ? ' (' + escapeDevHtml(r.user_role) + ')' : ''));
    if (r.store)      ctxBits.push('<strong>Store:</strong> ' + escapeDevHtml(r.store));
    if (r.country)    ctxBits.push('<strong>Country:</strong> ' + escapeDevHtml(r.country));
    if (r.screen)     ctxBits.push('<strong>Screen:</strong> ' + escapeDevHtml(r.screen));
    if (r.app_version) ctxBits.push('<strong>Version:</strong> ' + escapeDevHtml(r.app_version));
    if (r.user_agent) ctxBits.push('<strong>UA:</strong> <span style="font-family:monospace;font-size:11px;word-break:break-all">' + escapeDevHtml(r.user_agent) + '</span>');

    detail =
      '<div style="border-top:1px solid var(--border);margin-top:10px;padding-top:12px">' +
        '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:14px">' +
          '<div>' +
            '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px">1. Doing</div>' +
            '<div style="font-size:13px;line-height:1.5;white-space:pre-wrap">' + escapeDevHtml(r.doing || '—') + '</div>' +
          '</div>' +
          '<div>' +
            '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px">2. Expected</div>' +
            '<div style="font-size:13px;line-height:1.5;white-space:pre-wrap">' + escapeDevHtml(r.expected || '—') + '</div>' +
          '</div>' +
          '<div>' +
            '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px">3. Actual</div>' +
            '<div style="font-size:13px;line-height:1.5;white-space:pre-wrap">' + escapeDevHtml(r.actual || '—') + '</div>' +
          '</div>' +
        '</div>' +
        '<div style="background:#fafafa;border-radius:8px;padding:10px 12px;font-size:12px;line-height:1.8;margin-bottom:14px">' +
          ctxBits.join(' · ') +
        '</div>' +
        (shotCount > 0
          ? '<div style="margin-bottom:14px">' +
              '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">Screenshots (' + shotCount + ')</div>' +
              '<div id="devShots-' + r.id + '" data-shots-pending="' + r.id + '" style="display:flex;flex-wrap:wrap;gap:8px">' +
                '<span style="font-size:12px;color:var(--sub)">Loading…</span>' +
              '</div>' +
            '</div>'
          : '') +
        '<div style="margin-bottom:12px">' +
          '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px">Admin notes</div>' +
          '<textarea id="devNotes-' + r.id + '" rows="2" style="width:100%;padding:8px 10px;border:1.5px solid var(--border);border-radius:8px;font-family:inherit;font-size:13px;box-sizing:border-box;resize:vertical">' + escapeDevHtml(r.admin_notes || '') + '</textarea>' +
          '<button class="btn btn-outline btn-sm" style="margin-top:6px" onclick="Uploads.devReports.saveDevNotes(' + r.id + ')">Save notes</button>' +
        '</div>' +
        '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
          (r.status === 'new'
            // v126.86 — ✓ Mark Done opens the CLOSE panel (email the store what
            // was done, or close silently) instead of instantly toggling.
            ? '<button class="btn btn-green" onclick="Uploads.devReports.toggleClosePanel(' + r.id + ')">✓ Mark Done</button>'
            : '<button class="btn btn-outline" onclick="Uploads.devReports.toggleDevStatus(' + r.id + ',\'new\')">↩ Reopen</button>'
          ) +
          // v126.86 — ✉️ More info: emails the store a question; their REPLY
          // rides the letterbox back into the HO unmatched-mail triage.
          (r.store ? '<button class="btn btn-outline" onclick="Uploads.devReports.toggleInfoPanel(' + r.id + ')">✉️ More info</button>' : '') +
          // v118.x: bug-triage agent (Phase 1). Disabled while a request
          // is in flight to make repeat clicks a no-op (also handled at
          // the runTriage level for safety).
          // v126.84 — 🚀 Send to Claude: files this report as a GitHub issue;
          // Claude Code runs in the cloud, asks questions as issue comments
          // (GitHub mobile pings Matt) and opens a PR to staging.
          '<button class="btn btn-outline" onclick="Uploads.devReports.sendToClaude(' + r.id + ')">🚀 Send to Claude</button>' +
          '<button class="btn btn-outline" onclick="Uploads.devReports.runTriage(\'feedback\',' + r.id + ')"' +
            (_triageInFlight.has('feedback:' + r.id) ? ' disabled' : '') +
            '>🤖 Triage</button>' +
        '</div>' +
        // v126.86 — the two close-the-loop panels (hidden until their button).
        '<div id="devClose-' + r.id + '" style="display:none;margin-top:10px;background:#f0faf4;border:1.5px solid var(--green,#009E3D);border-radius:10px;padding:10px 12px">' +
          '<div style="font-size:12px;font-weight:700;margin-bottom:6px">Close this report' + (r.store ? ' — tell ' + escapeDevHtml(reportRecipientLabel(r)) + ' what was done:' : ':') + '</div>' +
          '<textarea id="devCloseMsg-' + r.id + '" rows="2" placeholder="e.g. Fixed in today\'s update - refresh the app and the Send button stays on screen." style="width:100%;padding:8px 10px;border:1.5px solid var(--border);border-radius:8px;font-family:inherit;font-size:13px;box-sizing:border-box;resize:vertical"></textarea>' +
          '<div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap">' +
            (r.store ? '<button class="btn btn-green btn-sm" onclick="Uploads.devReports.notifyStore(' + r.id + ',\'done\')">✓ Close &amp; email ' + (reportRecipientLabel(r) === r.store ? 'store' : 'them') + '</button>' : '') +
            '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.toggleDevStatus(' + r.id + ',\'done\')">Close without email</button>' +
            '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.toggleClosePanel(' + r.id + ')">Cancel</button>' +
          '</div></div>' +
        '<div id="devInfo-' + r.id + '" style="display:none;margin-top:10px;background:#fffbe8;border:1.5px solid #d9b23a;border-radius:10px;padding:10px 12px">' +
          '<div style="font-size:12px;font-weight:700;margin-bottom:6px">Ask ' + escapeDevHtml(reportRecipientLabel(r)) + ' for more info — they reply to the email and it lands back at Head Office:</div>' +
          '<textarea id="devInfoMsg-' + r.id + '" rows="2" placeholder="e.g. Which screen were you on, and can you send a photo of the error?" style="width:100%;padding:8px 10px;border:1.5px solid var(--border);border-radius:8px;font-family:inherit;font-size:13px;box-sizing:border-box;resize:vertical"></textarea>' +
          '<div style="display:flex;gap:8px;margin-top:8px">' +
            '<button class="btn btn-green btn-sm" onclick="Uploads.devReports.notifyStore(' + r.id + ',\'more_info\')">✉️ Send question</button>' +
            '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.toggleInfoPanel(' + r.id + ')">Cancel</button>' +
          '</div></div>' +
        renderTriagePanel('feedback', r.id) +
      '</div>';
  }

  return '<div style="background:#fff;border:1px solid var(--border);border-radius:10px;padding:12px 14px;margin-bottom:10px">' +
    '<div style="display:flex;align-items:center;gap:10px;cursor:pointer" onclick="Uploads.devReports.toggleDevRow(' + r.id + ')">' +
      '<span style="font-size:12px;color:var(--sub);width:14px;text-align:center">' + caret + '</span>' +
      '<div style="flex:1;min-width:0">' +
        '<div style="display:flex;gap:8px;align-items:center;margin-bottom:2px;flex-wrap:wrap">' +
          statusBadge +
          '<span style="font-size:12px;color:var(--sub)">' + submitted + '</span>' +
          '<span style="font-size:12px;color:var(--sub)">·</span>' +
          '<span style="font-size:12px;font-weight:600">' + escapeDevHtml(r.user_name || '—') + '</span>' +
          (r.store ? '<span style="font-size:12px;color:var(--sub)">·</span><span style="font-size:12px;color:var(--sub)">' + escapeDevHtml(r.store) + '</span>' : '') +
          (kindBadge ? '<span style="font-size:12px;color:var(--sub)">·</span>' + kindBadge : '') +
          (shotBadge ? '<span style="font-size:12px;color:var(--sub)">·</span>' + shotBadge : '') +
          doneHint +
        '</div>' +
        '<div style="font-size:13px;color:var(--text);line-height:1.4">' + escapeDevHtml(summary) + '</div>' +
      '</div>' +
    '</div>' +
    detail +
  '</div>';
}

export function renderDevReports() {
  const list = document.getElementById('devReportsList');
  const countEl = document.getElementById('devCount');
  if (!list) return;

  const statusFilter = document.getElementById('devStatusFilter').value;
  const storeFilter = document.getElementById('devStoreFilter').value;

  let rows = _devReports.slice();
  if (statusFilter) rows = rows.filter(r => r.status === statusFilter);
  if (storeFilter)  rows = rows.filter(r => r.store === storeFilter);

  if (countEl) countEl.textContent = rows.length + (rows.length === 1 ? ' report' : ' reports');

  if (rows.length === 0) {
    list.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:40px;text-align:center;background:#fafafa;border-radius:10px">No reports match your filters.</div>';
    return;
  }

  list.innerHTML = rows.map(r => renderDevRow(r)).join('');

  // v124.16: hydrate screenshot strips for any just-expanded report. The row
  // HTML only carries a count; the images load on demand here so a big
  // feedback list stays light.
  const pending = list.querySelectorAll('[data-shots-pending]');
  for (let i = 0; i < pending.length; i++) {
    const el = pending[i];
    const id = el.getAttribute('data-shots-pending');
    el.removeAttribute('data-shots-pending');
    loadDevShots(id);
  }
}

// v124.16: fetch + render the screenshots attached to one report. Thumbnails
// click through to a full-screen zoom overlay (a data: URL can't be opened as a
// top-level navigation in Chrome, so we zoom in place rather than link out).
export async function loadDevShots(id) {
  const el = document.getElementById('devShots-' + id);
  if (!el) return;
  try {
    const rows = await DB.feedback.photos(id);
    if (!Array.isArray(rows) || rows.length === 0) {
      el.innerHTML = '<span style="font-size:12px;color:var(--sub)">No screenshots.</span>';
      return;
    }
    el.innerHTML = rows.map(function (p, i) {
      const src = escapeDevHtml(p.image_data || '');
      // v128.38 (Doha Landmark): PDF/Word attachments render as DOWNLOAD tiles —
      // a data:application/ URL can't preview in an <img>, and Chrome blocks it
      // as a top-level navigation, but an <a download> saves it fine.
      const m = /^data:application\/(pdf|msword|vnd\.openxmlformats[^;]*)/.exec(p.image_data || '');
      if (m) {
        const ext = m[1] === 'pdf' ? 'pdf' : (m[1] === 'msword' ? 'doc' : 'docx');
        return '<a href="' + src + '" download="attachment-' + (i + 1) + '.' + ext + '" ' +
          'style="width:120px;height:120px;display:inline-flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;' +
          'border:1px solid var(--border);border-radius:8px;font-size:12px;font-weight:800;text-decoration:none;color:var(--ink,#111)">' +
          '📎<span>' + ext.toUpperCase() + '</span><span style="font-weight:400;font-size:11px;color:var(--sub)">download</span></a>';
      }
      return '<img class="dev-shot-thumb-img" src="' + src + '" alt="screenshot" ' +
        'style="width:120px;height:120px;object-fit:cover;border:1px solid var(--border);border-radius:8px;cursor:zoom-in" loading="lazy">';
    }).join('');
    const imgs = el.querySelectorAll('img.dev-shot-thumb-img');
    for (let i = 0; i < imgs.length; i++) {
      imgs[i].onclick = function () { devShotZoom(this.src); };
    }
  } catch (e) {
    el.innerHTML = '<span style="font-size:12px;color:var(--red)">Failed to load screenshots.</span>';
  }
}

export function devShotZoom(src) {
  let ov = document.getElementById('devShotZoomOverlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'devShotZoomOverlay';
    ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:9999;display:flex;align-items:center;justify-content:center;padding:24px;cursor:zoom-out';
    ov.onclick = function () { ov.style.display = 'none'; ov.innerHTML = ''; };
    document.body.appendChild(ov);
  }
  ov.innerHTML = '';
  const big = document.createElement('img');
  big.src = src;
  big.style.cssText = 'max-width:100%;max-height:100%;border-radius:8px;box-shadow:0 8px 40px rgba(0,0,0,.5)';
  ov.appendChild(big);
  ov.style.display = 'flex';
}

export async function loadDevReports() {
  const list = document.getElementById('devReportsList');
  if (list) list.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">Loading reports…</div>';
  try {
    // No status filter on load — filter client-side so the user can flip filters without refetching
    const rows = await DB.feedback.list({});
    _devReports = Array.isArray(rows) ? rows : [];
    populateDevStoreFilter();
    renderDevReports();
  } catch (e) {
    if (list) list.innerHTML = '<div style="font-size:13px;color:var(--red);padding:20px;text-align:center">Failed to load: ' + (e && e.message ? e.message : 'unknown') + '</div>';
  }
}

export function toggleDevRow(id) {
  if (_devExpanded.has(id)) _devExpanded.delete(id);
  else _devExpanded.add(id);
  renderDevReports();
}

export async function toggleDevStatus(id, newStatus) {
  try {
    const patch = { status: newStatus };
    if (newStatus === 'done' && currentAdmin && currentAdmin.name) patch.done_by = currentAdmin.name;
    const result = await DB.feedback.update(id, patch);
    // Server returns the updated row (as an array from Supabase PATCH) — refresh local copy
    const updated = Array.isArray(result) ? result[0] : result;
    const idx = _devReports.findIndex(r => String(r.id) === String(id));
    if (idx !== -1 && updated) _devReports[idx] = updated;
    renderDevReports();
    showToast(newStatus === 'done' ? 'Marked done' : 'Reopened', 'success');
  } catch (e) {
    showToast('Update failed: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
}

export async function saveDevNotes(id) {
  const ta = document.getElementById('devNotes-' + id);
  if (!ta) return;
  const notes = ta.value;
  try {
    const result = await DB.feedback.update(id, { admin_notes: notes });
    const updated = Array.isArray(result) ? result[0] : result;
    const idx = _devReports.findIndex(r => String(r.id) === String(id));
    if (idx !== -1 && updated) _devReports[idx] = updated;
    showToast('Notes saved', 'success');
  } catch (e) {
    showToast('Save failed: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
}

// v126.84 — 🚀 hand this report to Claude Code in the cloud. The Worker files
// a GitHub issue (@claude in the body wakes .github/workflows/claude.yml);
// questions come back as issue comments, the fix arrives as a PR to staging.
// Button-triggered by design: Matt stays the filter on what spends API budget.
export async function sendToClaude(id) {
  if (!confirm('Send this report to Claude to investigate and fix?\n\nIt opens a GitHub issue; Claude works on it in the cloud and opens a PR to staging. Any questions come to you as GitHub notifications.')) return;
  const token = adminToken();
  try {
    const res = await fetch(DB.workerUrl() + '/api/feedback/to-github', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.ok) {
      showToast((d && d.error) || 'Could not send to Claude (HTTP ' + res.status + ')', 'error');
      return;
    }
    showToast('Sent — Claude is on it (issue #' + d.number + '). Watch GitHub for questions and the PR.', 'success');
    try { window.open(d.url, '_blank'); } catch (e) { /* popup blocked — the link is in the notes */ }
    loadDevReports();   // re-render: the report notes now carry the issue link
  } catch (e) {
    showToast('Could not reach the Worker: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
}

// v126.86 — the close-the-loop panels (Matt: "e-mail stores when their bug is
// fixed... a text box to say what i have done... a 'more info' button").
export function toggleClosePanel(id) {
  var p = document.getElementById('devClose-' + id);
  var q = document.getElementById('devInfo-' + id);
  if (q) q.style.display = 'none';
  if (p) p.style.display = (p.style.display === 'none' || !p.style.display) ? 'block' : 'none';
}
export function toggleInfoPanel(id) {
  var p = document.getElementById('devInfo-' + id);
  var q = document.getElementById('devClose-' + id);
  if (q) q.style.display = 'none';
  if (p) p.style.display = (p.style.display === 'none' || !p.style.display) ? 'block' : 'none';
}
export async function notifyStore(id, kind) {
  var ta = document.getElementById((kind === 'done' ? 'devCloseMsg-' : 'devInfoMsg-') + id);
  var message = ta ? String(ta.value || '').trim() : '';
  if (kind === 'more_info' && !message) { showToast('Type the question for the store first', 'error'); return; }
  const token = adminToken();
  try {
    const res = await fetch(DB.workerUrl() + '/api/feedback/notify', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, kind: kind, message: message }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.ok) { showToast((d && d.error) || 'Email failed (HTTP ' + res.status + ')', 'error'); return; }
    showToast(kind === 'done' ? 'Closed — the store has been emailed 💚' : 'Question sent to ' + d.to, 'success');
    loadDevReports();   // reflect the new status + the stamped note
  } catch (e) {
    showToast('Could not reach the Worker: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
}

export function switchDevSubTab(sub) {
  // v60: 5 panels — status, feedback, errors, audit, reveals
  // v79.15.1: 6 panels — added writeAudit (replaces planned rotaClears)
  // v80.17:   7 panels — added smoke (production health check)
  const panels = {
    feedback:   document.getElementById('devFeedbackPanel'),
    errors:     document.getElementById('devErrorsPanel'),
    audit:      document.getElementById('devAuditPanel'),
    reveals:    document.getElementById('devRevealsPanel'),
    writeAudit: document.getElementById('devWriteAuditPanel'),
    smoke:      document.getElementById('devSmokePanel'),
    ingest:     document.getElementById('adminIngestPanel'), // v127.7 (K4): admin-side feed-health mirror; the dev console's ingest view has its own id and is untouched
  };
  const tabs = {
    feedback:   document.getElementById('devSubTabFeedback'),
    errors:     document.getElementById('devSubTabErrors'),
    audit:      document.getElementById('devSubTabAudit'),
    reveals:    document.getElementById('devSubTabReveals'),
    writeAudit: document.getElementById('devSubTabWriteAudit'),
    smoke:      document.getElementById('devSubTabSmoke'),
    ingest:     document.getElementById('devSubTabIngest'),
  };
  // The admin "Audit" tab carries only a subset of these panels (the
  // technical ones — status/feedback/errors/smoke — moved to the dev
  // console). So require only the REQUESTED panel to exist, and skip any
  // panel that isn't on this page rather than bailing on the whole switch.
  if (!panels[sub] || !tabs[sub]) return;
  Object.keys(panels).forEach(k => {
    if (!panels[k] || !tabs[k]) return;
    panels[k].style.display = (k === sub) ? 'block' : 'none';
    tabs[k].className = (k === sub) ? 'btn btn-green' : 'btn btn-outline';
    tabs[k].style.borderRadius = '8px 8px 0 0';
  });
  if (sub === 'errors') {
    if (!_errorReports.length) loadErrorReports();
    else renderErrorReports();
    } else if (sub === 'audit') {
    initDevAuditFilters();
  } else if (sub === 'reveals') {
    loadDevReveals();
  } else if (sub === 'writeAudit') {
    populateWriteAuditStoreFilter();
    loadDevWriteAudit();
  } else if (sub === 'ingest') {
    loadIngestHealth();
  }
  // smoke panel: no auto-load — user clicks "Run" explicitly
}

export async function loadDevWriteAudit() {
  const el = document.getElementById('devWriteAuditContent');
  if (!el) return;
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">Loading…</div>';
  try {
    const storeFilter  = document.getElementById('writeAuditStoreFilter');
    const actionFilter = document.getElementById('writeAuditActionFilter');
    const opts = {};
    if (storeFilter && storeFilter.value)   opts.store  = storeFilter.value;
    if (actionFilter && actionFilter.value) opts.action = actionFilter.value;

    const rows = await DB.writeAuditLog.list(opts);
    if (!rows || !rows.length) {
      el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">No audit entries match the current filter.</div>';
      return;
    }

    // Action label + colour scheme. Balance edits get a warning tint.
    const ACTION_LABELS = {
      clear_week:      { label: '🗑️ Clear week',      color: '#a23' },
      rota_save:       { label: '📅 Rota save',       color: 'var(--text)' },
      balance_edit:    { label: '⚠️ Balance edit',    color: '#a60' },
      delivery_edit:   { label: '📦 Delivery edit',   color: 'var(--text)' },
      delivery_delete: { label: '📦 Delivery delete', color: '#a23' },
      log_edit:        { label: '📝 Log edit',        color: 'var(--text)' },
    };

    const fmtDate = ts => {
      try {
        const d = new Date(ts);
        return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch (e) { return ts; }
    };

    const fmtDetail = (action, detail) => {
      if (!detail) return '';
      try {
        if (action === 'balance_edit' && Array.isArray(detail.edits)) {
          return detail.edits.length + ' staff edited (year ' + (detail.year || '?') + ')';
        }
        if (action === 'rota_save' && detail.total_shifts) {
          return detail.total_shifts + ' shifts saved';
        }
        if (action === 'delivery_edit' && Array.isArray(detail.days)) {
          return detail.days.join(', ');
        }
        if (action === 'log_edit' && Array.isArray(detail.changed_fields)) {
          return 'fields: ' + detail.changed_fields.join(', ');
        }
        return JSON.stringify(detail).slice(0, 80);
      } catch (e) { return ''; }
    };

    const html = ['<table class="data-table" style="width:100%;font-size:13px"><thead><tr>',
      '<th style="text-align:start;padding:8px">When</th>',
      '<th style="text-align:start;padding:8px">Action</th>',
      '<th style="text-align:start;padding:8px">Store</th>',
      '<th style="text-align:start;padding:8px">Acted by</th>',
      '<th style="text-align:end;padding:8px"># rows</th>',
      '<th style="text-align:start;padding:8px">Detail</th>',
      '</tr></thead><tbody>'];
    rows.forEach(function(r){
      const lab = ACTION_LABELS[r.action] || { label: r.action, color: 'var(--sub)' };
      html.push('<tr>',
        '<td style="padding:8px;color:var(--sub);font-size:12px;white-space:nowrap">', escapeHtml(fmtDate(r.acted_at)), '</td>',
        '<td style="padding:8px;color:', lab.color, ';font-weight:600">', escapeHtml(lab.label), '</td>',
        '<td translate="no" style="padding:8px">', escapeHtml(r.store || '—'), '</td>',
        '<td translate="no" style="padding:8px">', escapeHtml(r.acted_by_staff_name || '(unknown)'), '</td>',
        '<td style="padding:8px;text-align:end;font-variant-numeric:tabular-nums">', String(r.affected_rows || 0), '</td>',
        '<td style="padding:8px;color:var(--sub);font-size:12px">', escapeHtml(fmtDetail(r.action, r.detail)), '</td>',
        '</tr>');
    });
    html.push('</tbody></table>');
    el.innerHTML = html.join('');
  } catch (e) {
    el.innerHTML = '<div style="font-size:13px;color:var(--red);padding:20px;text-align:center">Failed to load: ' + escapeHtml(e.message || 'unknown') + '</div>';
  }
}

export async function loadDevReveals() {
  const el = document.getElementById('devRevealsContent');
  if (!el) return;
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">Loading…</div>';
  try {
    const rows = await DB.staff.pinReveals();
    if (!rows || !rows.length) {
      el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">No PIN reveals recorded yet.</div>';
      return;
    }
    const fmtDate = ts => {
      try {
        const d = new Date(ts);
        return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch(e) { return ts; }
    };
    const rowsHtml = rows.map(r => {
      const staffName = (r.staff && r.staff.name) || ('Staff #' + r.staff_id);
      return '<tr>'
        + '<td style="padding:8px 12px;font-size:13px;white-space:nowrap">' + escapeHtml(fmtDate(r.revealed_at)) + '</td>'
        + '<td translate="no" style="padding:8px 12px;font-size:13px;font-weight:600">' + escapeHtml(r.revealed_by_name || '—') + '</td>'
        + '<td translate="no" style="padding:8px 12px;font-size:13px">' + escapeHtml(staffName) + '</td>'
        + '</tr>';
    }).join('');
    el.innerHTML = '<table style="width:100%;border-collapse:collapse;background:#fff;border-radius:8px;overflow:hidden">'
      + '<thead><tr style="background:#f8f9fa;border-bottom:1px solid var(--border)">'
      + '<th style="padding:10px 12px;text-align:start;font-size:11px;text-transform:uppercase;color:var(--sub);font-weight:600">When</th>'
      + '<th style="padding:10px 12px;text-align:start;font-size:11px;text-transform:uppercase;color:var(--sub);font-weight:600">Revealed by</th>'
      + '<th style="padding:10px 12px;text-align:start;font-size:11px;text-transform:uppercase;color:var(--sub);font-weight:600">Staff</th>'
      + '</tr></thead><tbody>' + rowsHtml + '</tbody></table>'
      + '<div style="font-size:11px;color:var(--sub);padding:8px 4px;text-align:end">' + rows.length + ' reveal' + (rows.length === 1 ? '' : 's') + ' shown (most recent 200).</div>';
  } catch(e) {
    console.error('[reveals] load failed:', e.message);
    el.innerHTML = '<div style="font-size:13px;color:var(--red);padding:20px;text-align:center">Couldn\'t load PIN reveals: ' + escapeHtml(e.message) + '</div>';
  }
}

// ── RETIRED (v127.5, Matt-approved dev-tab review) ─────────────
// The v59 Status panel (renderDevStatus + loadTriageScorecard +
// loadFeedbackInsights) and the superseded loadStaffChanges/
// renderStaffChanges copy were deleted here: the panel lost its host
// HTML in the v102/dev-console split (unreachable since), its
// scorecard/insights live on as Overview agent cards in src/agents,
// and the REAL Amanah staff-changes reviewer is src/staffAdmin.
// ── Ingest health (v124.63) ─────────────────────────────────
// Read-only monitor over the Cubes/Amanah push feeds that land in
// `ingest_inbox`. Per feed (sales / staff): when it was last received, whether
// the last capture succeeded or failed, validated-vs-rejected counts over the
// window, and the most recent failings (row/field/message). Deterministic on
// the worker side (no AI); fetched with DB.workerUrl() + the admin Bearer, the
// same pattern as loadTriageScorecard / loadFeedbackInsights above.
// v129.6 (mobile review) — ONE token read for this module's direct fetches.
// The Ingest view read sessionStorage with no fallback, so a phone whose
// sessionStorage was empty/stale errored on Ingest alone while Feedback and
// Errors (which ride DB's in-memory token, primed at login) kept working.
// Same fallback chain dev.html's own boot uses.
// v136.12: the body is now the ONE shared read (src/shared/admin-token.js): DB token, then this tab,
// then the remembered copy. The name stays, so every call below is unchanged.
function adminToken() {
  return sharedAdminToken();
}

export async function loadIngestHealth() {
  const el = document.getElementById('ingestHealthContent');
  if (!el) return;
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">Loading…</div>';
  try {
    const token = adminToken();
    const res = await fetch(DB.workerUrl() + '/api/admin/ingest-health', { cache: 'no-store',
      headers: { 'Authorization': 'Bearer ' + token },
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      el.innerHTML = '<span style="color:var(--red);word-break:break-word">Could not load ingest health: ' +
        escapeDevHtml((payload && payload.error) || ('HTTP ' + res.status)) + '</span>';
      return;
    }
    el.innerHTML = renderIngestHealth(payload);
  } catch (e) {
    el.innerHTML = '<span style="color:var(--red);word-break:break-word">Could not load ingest health: ' +
      escapeDevHtml((e && e.message) || String(e)) + '</span>';
  }
}

// ── Clear handled rejections (v125.35) ──────────────────────
// Matt: "how can we see what has been rejected or clear once done?" The SEE
// half is the Recent-failings list below each feed card (last 14 days, with
// the v125.34 record refs). This is the CLEAR half: once a rejection has been
// chased (partner fixed + re-pushed, or noted), the per-feed "✔ Clear" button
// flips its inbox rows 'rejected' → 'dismissed' so the count and list reset.
// A status flip on the server, never a delete — the rows stay in ingest_inbox
// as history. Confirm-gated; reloads the panel on success.
export async function clearIngestRejections(feed) {
  const label = feed ? (feed + ' feed') : 'all feeds';
  if (!confirm('Clear the rejected entries for the ' + label + '?\n\nThey stop showing here but stay in the history (nothing is deleted).')) return;
  try {
    const token = adminToken();
    const res = await fetch(DB.workerUrl() + '/api/admin/ingest/clear-rejections', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify(feed ? { feed: feed } : {}),
    });
    const payload = await res.json().catch(function () { return {}; });
    if (!res.ok || !payload || payload.ok !== true) {
      alert('Could not clear rejections: ' + ((payload && payload.error) || ('HTTP ' + res.status)));
      return;
    }
    await loadIngestHealth();
  } catch (e) {
    alert('Could not clear rejections: ' + ((e && e.message) || String(e)));
  }
}

// ── Apply captured Cubes sales to live reports ──────────────
// As of v124.68 SALES APPLY AUTOMATICALLY on capture (worker.js schedules the
// apply via ctx.waitUntil right after the partner's { ok:true }). The old manual
// "▶️ Apply now" button was removed — Matt just glances at the Applied line in the
// health panel each morning to confirm it went through. The admin endpoint
// POST /api/admin/ingest/apply is KEPT server-side as a backstop (e.g. to re-apply
// rows left 'validated' because the store-code map was missing a code at capture
// time), but there is no longer a UI control that calls it.

// v127.40 — RESTORED: the v127.5 dev-tab retirement deleted these two as
// "orphaned Status-panel helpers", but the Ingest health cards below still
// call them — the Ingest tab died with "ingestRelTime is not defined" from
// the moment v127.5 deployed (Matt hit it 28 Jul, right as the ALGT sales
// feed came live). Recovered verbatim from the pre-v127.5 source.
// v129.6 — Safari strictness: PostgREST serialises timestamptz with SIX
// fractional digits; the spec (and older WebKit, strictly) allows three →
// "Invalid Date" on iPhones while Chrome shrugged. Trim before parsing.
function ingestParseTs(iso) {
  if (!iso) return NaN;
  try { return new Date(String(iso).replace(/\.(\d{3})\d+/, '.$1')).getTime(); }
  catch (e) { return NaN; }
}
function ingestRelTime(iso) {
  if (!iso) return 'never';
  const t = ingestParseTs(iso);
  if (isNaN(t)) return 'never';
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return secs + 's ago';
  const mins = Math.round(secs / 60);
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.round(hrs / 24);
  return days + 'd ago';
}
function ingestAbsTime(iso) {
  if (!iso) return '—';
  try {
    const t = ingestParseTs(iso);
    if (isNaN(t)) return '—';   // v129.6: was rendering "Invalid Date" on Safari
    return new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return iso; }
}

function renderIngestHealth(data) {
  if (!data || data.ok === false) {
    const reason = (data && data.reason) || (data && data.error) || 'the ingest inbox is not provisioned yet';
    return '<div style="font-size:13px;color:var(--sub)">Ingest health unavailable — ' + escapeDevHtml(reason) + '.</div>';
  }
  const feeds = (data && data.feeds) || {};
  // v129.6: the worker computes THREE feeds (ingest-health.js FEEDS) but this
  // renderer only listed two — top50 was silently invisible in both surfaces.
  const order = ['sales', 'staff', 'top50'];
  const windowDays = data.windowDays || 14;

  const overallOk = data.healthy;
  const banner = '<div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;font-size:13px">' +
    '<span style="font-weight:700;color:' + (overallOk ? '#2ee6a6' : '#ffc24b') + '">' +
      (overallOk ? '✅ Feeds healthy' : '⚠️ Attention needed') + '</span>' +
    '<span style="color:var(--sub)">· last ' + windowDays + ' days</span>' +
  '</div>';

  const cards = order.map(function (feed) {
    const f = feeds[feed] || { feed: feed, lastReceivedAt: null, lastStatus: null, validatedCount: 0, rejectedCount: 0, appliedCount: 0, lastAppliedAt: null, recentFailures: [] };
    // Status badge: OK (last validated/applied) / last rejected / none yet.
    let badge;
    if (!f.lastReceivedAt) {
      badge = '<span style="font-size:11px;font-weight:700;padding:3px 9px;border-radius:6px;background:rgba(72,89,126,.18);color:var(--sub)">⚪ None yet</span>';
    } else if (f.lastStatus === 'rejected') {
      badge = '<span style="font-size:11px;font-weight:700;padding:3px 9px;border-radius:6px;background:rgba(255,194,75,.16);color:#ffc24b">⚠️ Last rejected</span>';
    } else {
      badge = '<span style="font-size:11px;font-weight:700;padding:3px 9px;border-radius:6px;background:rgba(46,230,166,.16);color:#2ee6a6">✅ OK</span>';
    }

    const lastLine = f.lastReceivedAt
      ? (ingestRelTime(f.lastReceivedAt) + ' · ' + escapeDevHtml(ingestAbsTime(f.lastReceivedAt)))
      : 'No data received yet.';

    // Applied line — SALES ONLY. Sales apply automatically on capture (v124.68),
    // so this is what Matt glances at each morning to confirm the day's sales came
    // in AND landed in the live tables. Staff is never applied (review-then-approve),
    // so it shows no applied line.
    let appliedLine = '';
    if (feed === 'sales') {
      const applied = f.appliedCount || 0;
      appliedLine = '<div style="font-size:12px;color:var(--sub);margin-bottom:8px">Applied: <span style="color:var(--text)">' +
        applied + (applied === 1 ? ' row' : ' rows') +
        (f.lastAppliedAt ? ' · last ' + escapeDevHtml(ingestRelTime(f.lastAppliedAt)) : ' · none yet') +
        '</span></div>';
    }

    const failures = Array.isArray(f.recentFailures) ? f.recentFailures : [];
    let failuresHtml = '';
    if (failures.length) {
      failuresHtml = '<div style="margin-top:10px">' +
        '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">Recent failings</div>' +
        failures.map(function (fail) {
          const when = ingestRelTime(fail.at);
          const errs = Array.isArray(fail.errors) ? fail.errors : [];
          const items = errs.slice(0, 6).map(function (e) {
            const rowRef = (e.row === null || e.row === undefined) ? '' : ('row ' + escapeDevHtml(String(e.row)) + ' · ');
            const fieldRef = e.field ? ('<code style="font-size:11px">' + escapeDevHtml(String(e.field)) + '</code> ') : '';
            // v125.34 — WHICH record was rejected (store_code + date / emp_code),
            // stamped by the validator. Rejections captured before v125.34
            // predate the ref and show the row number only.
            const idRef = e.ref ? ('<div style="font-size:11px;color:var(--sub)">↳ ' + escapeDevHtml(String(e.ref)) + '</div>') : '';
            return '<li style="font-size:12px;line-height:1.5;color:var(--txt);word-break:break-word">' + rowRef + fieldRef + escapeDevHtml(String(e.message || '')) + idRef + '</li>';
          }).join('');
          const more = errs.length > 6 ? ('<li style="font-size:11px;color:var(--sub)">+' + (errs.length - 6) + ' more…</li>') : '';
          return '<div style="margin-bottom:8px">' +
            '<div style="font-size:11px;color:var(--sub)">' + escapeDevHtml(when) + ' · ' + (fail.error_count != null ? fail.error_count : errs.length) + ' error' + ((fail.error_count === 1 || errs.length === 1) ? '' : 's') + '</div>' +
            '<ul style="margin:3px 0 0;padding-inline-start:18px">' + items + more + '</ul>' +
          '</div>';
        }).join('') +
      '</div>';
    }

    return '<div style="background:#fff;border:1px solid var(--border);border-radius:10px;padding:14px 16px;margin-bottom:12px">' +
      '<div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">' +
        '<span style="font-size:14px;font-weight:700;text-transform:capitalize">' + escapeDevHtml(feed) + ' feed</span>' +
        badge +
      '</div>' +
      '<div style="font-size:12px;color:var(--sub);margin-bottom:8px">Last received: <span style="color:var(--text)">' + lastLine + '</span></div>' +
      appliedLine +
      '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
        '<span style="font-size:12px;background:rgba(46,230,166,.16);color:#2ee6a6;padding:3px 10px;border-radius:6px;font-weight:700">' + (f.validatedCount || 0) + ' validated</span>' +
        (feed === 'sales'
          ? '<span style="font-size:12px;background:rgba(46,230,166,.16);color:#2ee6a6;padding:3px 10px;border-radius:6px;font-weight:700">' + (f.appliedCount || 0) + ' applied</span>'
          : '') +
        '<span style="font-size:12px;background:' + ((f.rejectedCount || 0) > 0 ? 'rgba(255,92,122,.16);color:#ff5c7a' : 'rgba(72,89,126,.18);color:var(--sub)') + ';padding:3px 10px;border-radius:6px;font-weight:700">' + (f.rejectedCount || 0) + ' rejected</span>' +
        // v125.35 — clear handled rejections (status flip to 'dismissed'; history
        // kept). Only offered while there is something to clear.
        ((f.rejectedCount || 0) > 0
          ? '<button onclick="Uploads.devReports.clearIngestRejections(\'' + feed + '\')" style="font-size:12px;padding:3px 10px;border-radius:6px;border:1px solid var(--border);background:transparent;color:var(--sub);cursor:pointer;font-weight:700">✔ Clear</button>'
          : '') +
      '</div>' +
      failuresHtml +
    '</div>';
  }).join('');

  return banner + cards +
    '<div style="font-size:11px;color:var(--sub);text-align:end;padding:4px 2px">Loaded at ' + new Date().toLocaleTimeString() + '</div>';
}

export async function loadDevAudit() {
  const el = document.getElementById('devAuditContent');
  if (!el) return;
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">Loading…</div>';
  const week = document.getElementById('devAuditWeek').value;
  const store = document.getElementById('devAuditStore').value;
  try {
    const rows = await auditShifts(week, store || undefined);
    renderDevAudit(rows || []);
  } catch(e) {
    el.innerHTML = '<div style="font-size:13px;color:var(--red);padding:20px;text-align:center">Failed to load: ' + (e.message || 'unknown') + '</div>';
  }
}

export function renderDevAudit(rows) {
  const el = document.getElementById('devAuditContent');
  if (!el) return;
  if (!rows.length) {
    el.innerHTML = '<div class="card" style="padding:30px;text-align:center;color:var(--sub);font-size:13px">No edits found for this week. Default-published shifts (untouched after generation) are filtered out.</div>';
    return;
  }
  // Sort newest first
  rows.sort((a,b) => (b.edited_at || '').localeCompare(a.edited_at || ''));
  // Render as a table
  let html = '<div class="card" style="padding:0;overflow:hidden"><div class="table-wrap"><table class="data-table">';
  html += '<thead><tr><th>When</th><th>Edited by</th><th>Staff</th><th>Day</th><th>Shift</th><th>Store</th></tr></thead><tbody>';
  rows.forEach(r => {
    const when = r.edited_at ? new Date(r.edited_at).toLocaleString() : '—';
    const editor = r.edited_by || '—';
    const isAdmin = editor.indexOf('(admin)') !== -1;
    const editorTag = isAdmin
      ? '<span style="display:inline-block;padding:1px 7px;background:#dbeafe;color:#1e40af;border-radius:6px;font-size:10px;font-weight:700;margin-inline-start:4px">ADMIN</span>'
      : '';
    const staffName = (staffData.find(s => s.id === r.staff_id) || {}).name || ('Staff #' + r.staff_id);
    const shift = r.is_day_off ? 'Day off'
      : (r.start && r.finish ? r.start + '–' + r.finish + (r.lunch_at ? ' (lunch ' + r.lunch_at + ')' : '') : '—');
    const store = r.work_store || '—';
    html += '<tr>';
    html += '<td style="font-size:11px;color:var(--sub)">' + when + '</td>';
    html += '<td><strong translate="no">' + escapeDevHtml(editor.replace(' (admin)', '')) + '</strong>' + editorTag + '</td>';   // v132.85: staff-supplied
    html += '<td translate="no">' + escapeDevHtml(staffName) + '</td>';
    html += '<td>' + (r.day || '') + '</td>';
    html += '<td style="font-family:ui-monospace,monospace;font-size:12px">' + shift + '</td>';
    html += '<td translate="no" style="font-size:11px;color:var(--sub)">' + store + '</td>';
    html += '</tr>';
  });
  html += '</tbody></table></div></div>';
  html += '<div style="font-size:11px;color:var(--sub);padding:8px;text-align:center">' + rows.length + ' edits</div>';
  el.innerHTML = html;
}



// ════════════════════════════════════════════════════════════
// v104.0 additions — DevReports (v104 extension)
// ════════════════════════════════════════════════════════════
// Adds error-reports cluster + smoke runner. Completes the
// Dev admin tab — runSmokeTests powers the smoke sub-tab,
// error reports power the errors sub-tab.

export async function runSmokeTests() {
  const resultsEl = document.getElementById('smokeResults');
  if (!resultsEl) return;
  resultsEl.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">Running…</div>';

  const token = adminToken();
  if (!token) {
    resultsEl.innerHTML = '<div style="color:var(--red);padding:14px">No admin session — sign out and back in to refresh the token.</div>';
    return;
  }

  try {
    // v85.1: use the canonical DB.workerUrl() helper. Was previously a
    // ternary checking for a non-existent uppercase constant property on
    // DB, which fell through to a relative URL and hit Netlify 404s.
    // Same root cause and shape as the v80.13 Top 50 admin fix.
    const res = await fetch(DB.workerUrl() + '/api/admin/smoke', { cache: 'no-store',
      headers: { 'Authorization': 'Bearer ' + token },
    });
    if (!res.ok) {
      resultsEl.innerHTML = '<div style="color:var(--red);padding:14px">HTTP ' + res.status + ' — ' + escapeHtml(await res.text()) + '</div>';
      return;
    }
    const data = await res.json();
    const s = data.summary || {};

    // Header banner — green if all pass, red if any fail/error.
    const headerColor = (s.fail > 0 || s.error > 0) ? '#b91c1c' : '#16a34a';
    const headerIcon  = (s.fail > 0 || s.error > 0) ? '❌' : '✅';
    let html = '<div style="background:' + headerColor + '14;border:1px solid ' + headerColor + '40;border-radius:10px;padding:12px 14px;margin-bottom:14px">' +
      '<div style="font-weight:700;color:' + headerColor + ';font-size:14px">' + headerIcon + ' ' +
      s.pass + ' passed' +
      (s.fail  ? ', ' + s.fail  + ' failed'  : '') +
      (s.error ? ', ' + s.error + ' errored' : '') +
      ' (worker ' + escapeHtml(data.worker_version || '?') + ')</div>' +
      '<div style="font-size:12px;color:var(--sub);margin-top:4px">Run at ' + new Date(data.run_at).toLocaleString() + '</div>' +
    '</div>';

    // Per-check rows
    html += '<table class="data-table" style="font-size:13px">' +
      '<thead><tr><th style="width:60px">Status</th><th>Check</th><th>Detail</th></tr></thead><tbody>';
    (data.checks || []).forEach(c => {
      const color = c.status === 'pass'  ? '#16a34a' :
                    c.status === 'fail'  ? '#b91c1c' :
                    /* error */            '#d97706';
      const icon  = c.status === 'pass'  ? '✓' :
                    c.status === 'fail'  ? '✗' :
                    /* error */            '⚠';
      html += '<tr>' +
        '<td style="padding:8px 12px;border-bottom:1px solid var(--border);color:' + color + ';font-weight:700;font-size:16px;text-align:center">' + icon + '</td>' +
        '<td style="padding:8px 12px;border-bottom:1px solid var(--border);font-weight:600">' + escapeHtml(c.name) + '</td>' +
        '<td style="padding:8px 12px;border-bottom:1px solid var(--border);color:var(--sub);font-size:12px">' + escapeHtml(c.detail || '') + '</td>' +
      '</tr>';
    });
    html += '</tbody></table>';

    resultsEl.innerHTML = html;
  } catch (e) {
    resultsEl.innerHTML = '<div style="color:var(--red);padding:14px">Network error: ' + escapeHtml(e.message) + '</div>';
  }
}

export async function loadErrorReports() {
  const list = document.getElementById('devErrorsList');
  if (list) list.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:20px;text-align:center">Loading…</div>';
  try {
    const rows = await DB.errors.list({});
    _errorReports = Array.isArray(rows) ? rows : [];
    updateErrorBadge();
    renderErrorReports();
  } catch (e) {
    if (list) list.innerHTML = '<div style="font-size:13px;color:var(--red);padding:20px;text-align:center">Failed to load: ' + Uploads.devReports.escapeDevHtml(e && e.message ? e.message : 'unknown') + '</div>';
  }
}

// Bulk-dismiss: mark every error matching the CURRENT view's filter (status/app)
// as 'ignored' in one request. Confirm-gated on the visible count so the admin
// knows exactly what they are clearing (it clears what's on screen, not the whole
// table, unless the filter is set to All/Both). Mirrors loadTriageScorecard's
// explicit DB.workerUrl() + Bearer fetch — same worker-call convention as the
// other admin actions in this module.
export async function clearAllErrors() {
  const statusFilter = document.getElementById('errStatusFilter').value;
  const appFilter    = document.getElementById('errAppFilter').value;
  // The rendered count already reflects both filters (see renderErrorReports).
  let visible = _errorReports.slice();
  if (statusFilter) visible = visible.filter(r => r.status === statusFilter);
  if (appFilter)    visible = visible.filter(r => r.app === appFilter);
  const n = visible.length;
  if (n === 0) { showToast('Nothing to clear', 'info'); return; }
  const scope = statusFilter ? ('"' + statusFilter + '"') : 'all statuses';
  const appScope = appFilter ? (appFilter === 'admin' ? ' admin-app' : ' staff-app') : '';
  if (!confirm('Clear all ' + n + appScope + ' ' + scope + ' error' + (n === 1 ? '' : 's') + '? This marks them as Ignored.')) return;
  try {
    const token = adminToken();
    const payload = { to: 'ignored' };
    if (statusFilter) payload.status = statusFilter;
    if (appFilter)    payload.app = appFilter;
    const res = await fetch(DB.workerUrl() + '/api/errors/clear', { cache: 'no-store',
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body || !body.ok) {
      throw new Error((body && body.error) || ('HTTP ' + res.status));
    }
    showToast('Cleared ' + (typeof body.cleared === 'number' ? body.cleared : n) + ' error' + (body.cleared === 1 ? '' : 's'), 'success');
    await loadErrorReports();
  } catch (e) {
    showToast('Clear failed: ' + ((e && e.message) ? e.message : 'unknown'), 'error');
  }
}

export function renderErrorReports() {
  const list = document.getElementById('devErrorsList');
  const countEl = document.getElementById('errCount');
  if (!list) return;

  const statusFilter = document.getElementById('errStatusFilter').value;
  const appFilter    = document.getElementById('errAppFilter').value;
  const groupMode    = document.getElementById('errGroupToggle').value;

  let rows = _errorReports.slice();
  if (statusFilter) rows = rows.filter(r => r.status === statusFilter);
  if (appFilter)    rows = rows.filter(r => r.app === appFilter);

  if (countEl) countEl.textContent = rows.length + (rows.length === 1 ? ' error' : ' errors');

  if (rows.length === 0) {
    list.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:40px;text-align:center;background:#fafafa;border-radius:10px">No errors match your filters. 🎉</div>';
    return;
  }

  if (groupMode === 'group') {
    // Group by fingerprint — most recent first, count + distinct users
    const groups = new Map();
    rows.forEach(r => {
      const key = r.fingerprint || (r.message + '|' + (r.stack || '').split('\n')[1] || '');
      if (!groups.has(key)) groups.set(key, { rows: [], latest: r });
      const g = groups.get(key);
      g.rows.push(r);
      if (new Date(r.occurred_at) > new Date(g.latest.occurred_at)) g.latest = r;
    });
    const sortedGroups = Array.from(groups.values()).sort((a,b) => new Date(b.latest.occurred_at) - new Date(a.latest.occurred_at));
    list.innerHTML = sortedGroups.map(g => renderErrorGroup(g)).join('');
  } else {
    list.innerHTML = rows.map(r => renderErrorRow(r, false)).join('');
  }
}

export function renderErrorGroup(g) {
  const latest = g.latest;
  const count = g.rows.length;
  const users = Array.from(new Set(g.rows.map(r => r.user_name).filter(Boolean)));
  const expanded = _errorsExpanded.has('g-' + (latest.fingerprint || latest.id));
  const statusColor = latest.status === 'new' ? '#dc2626' : latest.status === 'seen' ? '#f59e0b' : latest.status === 'fixed' ? '#16a34a' : '#6b7280';
  const appBadge = latest.app === 'admin' ? '<span style="background:#dbeafe;color:#1d4ed8;font-size:10px;font-weight:700;padding:1px 6px;border-radius:4px">ADMIN</span>' : '<span style="background:#dcfce7;color:#15803d;font-size:10px;font-weight:700;padding:1px 6px;border-radius:4px">STAFF</span>';
  const countChip = count > 1 ? '<span style="background:#fef3c7;color:#92400e;font-size:11px;font-weight:700;padding:2px 8px;border-radius:6px;margin-left:6px">×' + count + '</span>' : '';
  const when = new Date(latest.occurred_at).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' });
  // v132.85: the fingerprint comes from the TOKENLESS /api/errors, so it is
  // attacker-supplied. Safe characters only (it is a Set key and an onclick arg).
  const groupKey = 'g-' + String(latest.fingerprint || latest.id).replace(/[^A-Za-z0-9_.:-]/g, '_');

  let html =
    '<div style="background:#fff;border:1px solid var(--border);border-left:4px solid ' + statusColor + ';border-radius:10px;margin-bottom:10px;overflow:hidden">' +
    '<div onclick="Uploads.devReports.toggleErrorGroup(\'' + escapeAttr(groupKey) + '\')" style="padding:12px 16px;cursor:pointer;display:flex;align-items:center;gap:10px;flex-wrap:wrap">' +
      '<div style="font-size:14px;font-weight:600;flex:1;min-width:0;word-break:break-word">' + Uploads.devReports.escapeDevHtml(latest.message) + countChip + '</div>' +
      appBadge +
      '<span style="font-size:11px;color:var(--sub)">' + Uploads.devReports.escapeDevHtml(when) + '</span>' +
    '</div>';

  if (expanded) {
    html +=
      '<div style="padding:12px 16px;border-top:1px solid var(--border);background:#fafafa">' +
        '<div style="font-size:11px;color:var(--sub);margin-bottom:6px"><strong>' + count + '</strong> occurrence' + (count===1?'':'s') +
          (users.length ? ' · users: ' + Uploads.devReports.escapeDevHtml(users.slice(0,5).join(', ')) + (users.length > 5 ? '…' : '') : '') +
        '</div>' +
        '<div style="font-size:12px;margin-bottom:10px">' +
          '<strong>Latest:</strong> ' + Uploads.devReports.escapeDevHtml(latest.app_version || 'unknown version') +
          (latest.url ? ' · <code style="font-size:11px">' + Uploads.devReports.escapeDevHtml(latest.url) + '</code>' : '') +
          (latest.line ? ' · line ' + latest.line : '') +
        '</div>' +
        (latest.stack ? '<pre style="background:#fff;border:1px solid var(--border);border-radius:6px;padding:8px;font-size:11px;overflow-x:auto;max-height:200px;margin:0 0 10px 0;white-space:pre-wrap">' + Uploads.devReports.escapeDevHtml(latest.stack) + '</pre>' : '') +
        renderErrorActions(latest) +
      '</div>';
  }
  html += '</div>';
  return html;
}

export function renderErrorRow(r, showStack) {
  const expanded = _errorsExpanded.has('r-' + r.id);
  const statusColor = r.status === 'new' ? '#dc2626' : r.status === 'seen' ? '#f59e0b' : r.status === 'fixed' ? '#16a34a' : '#6b7280';
  const appBadge = r.app === 'admin' ? '<span style="background:#dbeafe;color:#1d4ed8;font-size:10px;font-weight:700;padding:1px 6px;border-radius:4px">ADMIN</span>' : '<span style="background:#dcfce7;color:#15803d;font-size:10px;font-weight:700;padding:1px 6px;border-radius:4px">STAFF</span>';
  const when = new Date(r.occurred_at).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' });
  const who = r.user_name ? Uploads.devReports.escapeDevHtml(r.user_name) + (r.store ? ' · ' + Uploads.devReports.escapeDevHtml(r.store) : '') : (r.store ? Uploads.devReports.escapeDevHtml(r.store) : '(unknown user)');

  let html =
    '<div style="background:#fff;border:1px solid var(--border);border-left:4px solid ' + statusColor + ';border-radius:10px;margin-bottom:8px;overflow:hidden">' +
    '<div onclick="Uploads.devReports.toggleErrorRow(' + r.id + ')" style="padding:10px 14px;cursor:pointer;display:flex;align-items:center;gap:10px;flex-wrap:wrap">' +
      '<div style="font-size:13px;font-weight:600;flex:1;min-width:0;word-break:break-word">' + Uploads.devReports.escapeDevHtml(r.message) + '</div>' +
      appBadge +
      '<span style="font-size:10px;color:var(--sub)">' + Uploads.devReports.escapeDevHtml(who) + '</span>' +
      '<span style="font-size:10px;color:var(--sub)">' + Uploads.devReports.escapeDevHtml(when) + '</span>' +
    '</div>';
  if (expanded) {
    html +=
      '<div style="padding:10px 14px;border-top:1px solid var(--border);background:#fafafa">' +
        '<div style="font-size:11px;color:var(--sub);margin-bottom:6px">' +
          Uploads.devReports.escapeDevHtml(r.app_version || 'unknown version') +
          (r.url ? ' · <code style="font-size:11px">' + Uploads.devReports.escapeDevHtml(r.url) + '</code>' : '') +
          (r.line ? ' · line ' + r.line : '') +
        '</div>' +
        (r.stack ? '<pre style="background:#fff;border:1px solid var(--border);border-radius:6px;padding:8px;font-size:11px;overflow-x:auto;max-height:200px;margin:0 0 10px 0;white-space:pre-wrap">' + Uploads.devReports.escapeDevHtml(r.stack) + '</pre>' : '') +
        renderErrorActions(r) +
      '</div>';
  }
  html += '</div>';
  return html;
}

export function renderErrorActions(r) {
  return (
    '<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:8px">' +
      '<label style="font-size:11px;color:var(--sub);font-weight:700">Status</label>' +
      '<select onchange="setErrorStatus(' + r.id + ', this.value)" class="filter-input" style="width:140px;font-size:12px">' +
        '<option value="new"'     + (r.status==='new'    ?' selected':'') + '>New</option>' +
        '<option value="seen"'    + (r.status==='seen'   ?' selected':'') + '>Seen</option>' +
        '<option value="fixed"'   + (r.status==='fixed'  ?' selected':'') + '>Fixed</option>' +
        '<option value="ignored"' + (r.status==='ignored'?' selected':'') + '>Ignored</option>' +
      '</select>' +
      // v118.x: bug-triage agent (Phase 1). Same handler as the
      // feedback panel; source='errors' so the worker pulls from the
      // error_log table and feeds the stack into Claude.
      '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.runTriage(\'errors\',' + r.id + ')"' +
        (_triageInFlight.has('errors:' + r.id) ? ' disabled' : '') +
        ' style="margin-left:auto">🤖 Triage</button>' +
    '</div>' +
    '<div style="margin-bottom:6px">' +
      '<label style="font-size:11px;color:var(--sub);font-weight:700;display:block;margin-bottom:4px">Notes (admin only)</label>' +
      '<textarea id="errNotes-' + r.id + '" rows="2" style="width:100%;padding:6px 10px;border:1.5px solid var(--border);border-radius:8px;font-family:inherit;font-size:12px;box-sizing:border-box;resize:vertical">' + Uploads.devReports.escapeDevHtml(r.admin_notes || '') + '</textarea>' +
      '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.saveErrorNotes(' + r.id + ')" style="margin-top:4px">Save notes</button>' +
    '</div>' +
    renderTriagePanel('errors', r.id)
  );
}

export function toggleErrorGroup(key) {
  if (_errorsExpanded.has(key)) _errorsExpanded.delete(key);
  else _errorsExpanded.add(key);
  renderErrorReports();
}

export function toggleErrorRow(id) {
  const key = 'r-' + id;
  if (_errorsExpanded.has(key)) _errorsExpanded.delete(key);
  else _errorsExpanded.add(key);
  renderErrorReports();
}

export async function saveErrorNotes(id) {
  const ta = document.getElementById('errNotes-' + id);
  if (!ta) return;
  try {
    const result = await DB.errors.update(id, { admin_notes: ta.value });
    const updated = Array.isArray(result) ? result[0] : result;
    const idx = _errorReports.findIndex(r => String(r.id) === String(id));
    if (idx !== -1 && updated) _errorReports[idx] = updated;
    showToast('Notes saved', 'success');
  } catch (e) {
    showToast('Save failed: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
}

// ═══════════════════════════════════════════════════════════
// BUG-TRIAGE AGENT — Phase 1 (v118.x)
// ═══════════════════════════════════════════════════════════
// Adds a [🤖 Triage] button to each row in the feedback and errors
// sub-tabs. Click → POST /api/admin/triage-report → render the
// diagnosis inline below the row.
//
// State lives in admin.html (`_triageInFlight`, `_triageResults`),
// matching the existing pattern for `_devReports` / `_errorReports`.
//
// Phase 1 is READ-ONLY: never modifies the source row, never opens
// a PR, never sends an email. The "clarify" outcome offers a
// Copy-to-clipboard so Matt can paste the draft into his mail client.
// Phase 2 (auto-PR creation) is gated on Phase 1 diagnosis quality.

// Stable cache key used by both _triageInFlight (Set) and
// _triageResults (Map). source ∈ {'feedback','errors'}, id is the row id.
function triageKey(source, id) { return source + ':' + id; }

// ── Live progress (v118+) ───────────────────────────────────
// Triage can take 1-2 minutes (multi-turn tool use + API rate-limiting),
// so a static "5-15s" spinner felt broken. We stream the agent's turns
// from the worker (NDJSON) and show them live, plus a ticking elapsed
// counter. Module-local — only this module reads them.
const _triageProgress = new Map();   // key -> latest progress event (+startedAt)
const _triageTimers   = {};          // key -> setInterval id for the elapsed ticker

// Map a worker progress phase to friendly, non-technical text.
function triageProgressMessage(prog) {
  prog = prog || {};
  const turn   = prog.turn ? (' · step ' + prog.turn) : '';
  const checks = (typeof prog.toolCalls === 'number' && prog.toolCalls > 0)
    ? (' · ' + prog.toolCalls + ' check' + (prog.toolCalls === 1 ? '' : 's'))
    : '';
  switch (prog.phase) {
    case 'loaded':   return 'Reading the report…';
    case 'fetching': return 'Fetching the source code…';
    case 'cache':    return 'Found a saved diagnosis…';
    case 'reading':  return 'Reading the code…';
    case 'model':    return 'Thinking…' + turn + checks;
    case 'tool':     return 'Searching the code' + (prog.lastTool ? ' (' + prog.lastTool + ')' : '') + turn + checks;
    case 'verify':   return 'Double-checking against staging…';
    default:         return 'Starting…';
  }
}

// Surgically refresh just the spinner line for one row (no list re-render,
// so textareas/focus survive). Safe no-op if the row is collapsed.
function updateTriageSpinner(source, id) {
  const key = triageKey(source, id);
  const el  = document.getElementById('triageSpin_' + source + '_' + id);
  if (!el) return;
  const prog    = _triageProgress.get(key) || {};
  const elapsed = prog.startedAt ? Math.round((Date.now() - prog.startedAt) / 1000) : 0;
  el.textContent = triageProgressMessage(prog) + '  (' + elapsed + 's)';
}

function startTriageTimer(source, id) {
  const key = triageKey(source, id);
  stopTriageTimer(key);
  _triageTimers[key] = setInterval(function () { updateTriageSpinner(source, id); }, 1000);
}
function stopTriageTimer(key) {
  if (_triageTimers[key]) { clearInterval(_triageTimers[key]); delete _triageTimers[key]; }
}

// Normalise a worker {type:'result',...} envelope (streamed or one-shot)
// into the result object the panel renders.
function triageResultFromEnvelope(env) {
  if (!env || !env.ok || !env.result) {
    return { kind: 'error', analysis: (env && env.error) || 'invalid triage response' };
  }
  const r = Object.assign({}, env.result);
  if (Array.isArray(env.missing) && env.missing.length) r._missing = env.missing;
  r._cached = !!env.cached;
  return r;
}

// Fallback for browsers/proxies that don't deliver a readable stream:
// the classic one-shot endpoint. Same contract, just no live progress.
async function runTriageOneShot(source, id, force, token) {
  const res = await fetch(DB.workerUrl() + '/api/admin/triage-report', { cache: 'no-store',
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + (token || adminToken()),
      'Content-Type':  'application/json',
    },
    body: JSON.stringify({ source, id, force: !!force }),
  });
  const payload = await res.json().catch(() => ({ error: 'invalid JSON from worker' }));
  if (!res.ok || !payload || !payload.ok) {
    return { kind: 'error', analysis: (payload && payload.error) || ('HTTP ' + res.status) };
  }
  return triageResultFromEnvelope(payload);
}

// Click handler for the [🤖 Triage] button. Idempotent — clicking while a
// request is already in flight is a no-op. Force=true bypasses the worker
// cache. Streams live progress from the worker; falls back to the one-shot
// endpoint if streaming isn't available.
export async function runTriage(source, id, force) {
  const key = triageKey(source, id);
  if (_triageInFlight.has(key) && !force) return;
  _triageInFlight.add(key);
  _triageProgress.set(key, { phase: 'start', startedAt: Date.now() });
  // Re-render so the row shows the spinner, then start the elapsed ticker.
  if (source === 'feedback') renderDevReports();
  else                       renderErrorReports();
  startTriageTimer(source, id);

  let finalResult = null;
  const token = adminToken();
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/triage-report-stream', { cache: 'no-store',
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type':  'application/json',
      },
      body: JSON.stringify({ source, id, force: !!force }),
    });
    if (res.ok && res.body && typeof res.body.getReader === 'function') {
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line); } catch (_e) { continue; }
          if (ev.type === 'progress') {
            const prev = _triageProgress.get(key) || {};
            _triageProgress.set(key, Object.assign({}, prev, ev));
            updateTriageSpinner(source, id);
          } else if (ev.type === 'result') {
            finalResult = triageResultFromEnvelope(ev);
          } else if (ev.type === 'error') {
            finalResult = { kind: 'error', analysis: ev.error || ('HTTP ' + (ev.status || '')) };
          }
        }
      }
    }
    // Streaming unavailable or yielded nothing — use the one-shot endpoint.
    if (!finalResult) finalResult = await runTriageOneShot(source, id, force, token);
  } catch (e) {
    // Stream/network error — try the one-shot endpoint before giving up.
    try {
      finalResult = await runTriageOneShot(source, id, force, token);
    } catch (e2) {
      finalResult = { kind: 'error', analysis: 'Network error: ' + ((e2 && e2.message) || String(e2)) };
    }
  } finally {
    stopTriageTimer(key);
    _triageProgress.delete(key);
    _triageInFlight.delete(key);
    if (finalResult) _triageResults.set(key, finalResult);
    if (source === 'feedback') renderDevReports();
    else                       renderErrorReports();
  }
}

// Copy the clarify-flow email draft to the clipboard. Uses the modern
// Clipboard API (HTTPS-only, but both staging and prod are HTTPS).
export async function copyTriageEmail(source, id) {
  const result = _triageResults.get(triageKey(source, id));
  const noun = (source === 'errors') ? 'notes' : 'email';
  if (!result || !result.email_draft) {
    showToast('No ' + noun + ' to copy', 'error');
    return;
  }
  try {
    await navigator.clipboard.writeText(result.email_draft);
    showToast((source === 'errors' ? 'Notes' : 'Email') + ' copied to clipboard', 'success');
  } catch (e) {
    showToast('Copy failed — select and copy manually', 'error');
  }
}

// ── "I'll take it from here" actions on a PROPOSED FIX ────────
// When the agent proposes a fix it did NOT auto-apply (confidence <7,
// or the auto-PR was blocked), the human needs a way to act on it.
// Matt named the three: hand it to Claude Code, open the PR anyway, or
// fall back to asking the store. These two need no backend; "Open PR
// anyway" (separate change) needs the worker endpoint + PAT scope.

// Copy a paste-ready prompt for Claude Code: the diagnosis + proposed
// diff + referenced files, framed as an instruction to verify, apply
// across the whole repo, test, and open a staging PR.
export async function copyTriageForClaudeCode(source, id) {
  const result = _triageResults.get(triageKey(source, id));
  if (!result || result.kind !== 'fix') {
    showToast('No proposed fix to copy', 'error');
    return;
  }
  const parts = [];
  parts.push(
    'The Shift bug-triage agent proposed the fix below for a ' + source +
    ' report (self-rated confidence ' +
    (typeof result.confidence === 'number' ? result.confidence + '/10' : 'not given') +
    ', so it was NOT auto-applied). Please verify it against the current code, ' +
    'apply it correctly (find EVERY occurrence, not just the first), run `npm test`, ' +
    'and open a draft PR to staging.'
  );
  parts.push('');
  parts.push('## Diagnosis');
  parts.push(result.analysis || '(none)');
  if (result.diff) {
    parts.push('');
    parts.push('## Proposed change (unified diff — verify before applying)');
    parts.push('```diff');
    parts.push(result.diff);
    parts.push('```');
  }
  if (Array.isArray(result.files_referenced) && result.files_referenced.length) {
    parts.push('');
    parts.push('## Files referenced');
    parts.push(result.files_referenced.join('\n'));
  }
  try {
    await navigator.clipboard.writeText(parts.join('\n'));
    showToast('Copied — paste into Claude Code', 'success');
  } catch (e) {
    showToast('Copy failed — select and copy manually', 'error');
  }
}

// "Ask the store" — when a proposed fix is unclear, or you'd rather
// check with the people who hit the problem, build a simple, ESL-
// friendly clarifying email from the original report and copy it.
// Feedback only: auto-captured errors have no human author to write to.
// Mirrors the agent's email rules — plain words, one question, ask for
// a photo first, reply in any language.
export async function askStoreEmail(source, id) {
  if (source !== 'feedback') {
    showToast('No store contact for auto-captured errors', 'error');
    return;
  }
  const rows = (typeof _devReports !== 'undefined' && Array.isArray(_devReports)) ? _devReports : [];
  const row = rows.find(r => String(r.id) === String(id));
  if (!row) {
    showToast('Could not find the original report', 'error');
    return;
  }
  const feature = row.screen ? row.screen : 'the app';
  const lines = [
    'Subject: Quick question about your report — ' + feature,
    '',
    'Hi team,',
    '',
    'Thank you for telling us about this. It really helps us fix the app.',
    '',
    'We have one question about the "' + feature + '" screen.',
    '',
    'When this happened, what did you see on the screen?',
    '',
    'You can reply in any of these ways:',
    '• Send a photo of your screen.',
    '• Write a few words — any language is fine. We will translate it.',
    '• Tell us: does this happen every time, or did it happen one time?',
    '',
    'Thank you,',
    'Shift Support',
  ];
  try {
    await navigator.clipboard.writeText(lines.join('\n'));
    showToast('Email copied to clipboard', 'success');
  } catch (e) {
    showToast('Copy failed — select and copy manually', 'error');
  }
}

// Render the triage result HTML block for one row. Returns '' if no
// triage has been run for this row yet (so the row HTML is unchanged
// until the user clicks the button).
//
// Visual contract:
//   - by_design → green pill, just the analysis
//   - clarify   → blue pill, analysis + email-draft block + Copy button
//   - fix       → orange pill, analysis + diff in a <pre>
//   - error     → red pill, analysis (which contains the error message)
//
// Each panel also shows a Regenerate button (force=true) and, if any
// source files came back missing on the worker side, a diagnostic note
// listing them. The missing-files note is the load-bearing UX cue for
// "the diagnosis is incomplete because Claude couldn't see the source".
export function renderTriagePanel(source, id) {
  const key       = triageKey(source, id);
  const inFlight  = _triageInFlight.has(key);
  const result    = _triageResults.get(key);

  if (inFlight) {
    const prog    = _triageProgress.get(key) || {};
    const elapsed = prog.startedAt ? Math.round((Date.now() - prog.startedAt) / 1000) : 0;
    const spinId  = 'triageSpin_' + source + '_' + id;
    // The text inside #spinId is updated live by updateTriageSpinner() on
    // each streamed progress event and once per second by the elapsed
    // ticker — so this initial render is just the starting state.
    return (
      '<div style="margin-top:12px;padding:10px 14px;background:#f3f4f6;border-radius:8px;font-size:13px;color:var(--sub);display:flex;align-items:center;gap:10px">' +
        '<span style="font-size:16px">🤖</span>' +
        '<span id="' + spinId + '">' +
          escapeDevHtml(triageProgressMessage(prog) + '  (' + elapsed + 's)') +
        '</span>' +
        '<span style="margin-left:auto;font-size:11px;color:var(--sub)">can take 1–2 min</span>' +
      '</div>'
    );
  }
  if (!result) return '';

  // Pill style + label per kind.
  let pillBg, pillFg, pillLabel;
  if (result.kind === 'by_design') {
    pillBg = '#d1fae5'; pillFg = '#065f46'; pillLabel = '✓ Working as intended';
  } else if (result.kind === 'clarify') {
    pillBg = '#dbeafe'; pillFg = '#1d4ed8'; pillLabel = '❓ Need more info';
  } else if (result.kind === 'fix') {
    pillBg = '#fef3c7'; pillFg = '#92400e'; pillLabel = '🛠️ Fix proposed';
  } else {
    pillBg = '#fee2e2'; pillFg = '#991b1b'; pillLabel = '⚠️ Triage error';
  }
  const pill =
    '<span style="background:' + pillBg + ';color:' + pillFg +
    ';padding:3px 10px;border-radius:6px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.3px">' +
    pillLabel + '</span>';

  const cachedTag = result._cached
    ? '<span style="font-size:10px;color:var(--sub)">cached</span>'
    : '';

  // Phase 2: when kind=fix, show the agent's confidence as a small tag
  // next to the pill. Sonnet self-rates 1-10; 7+ triggers an auto-PR.
  const confidenceTag = (result.kind === 'fix' && typeof result.confidence === 'number')
    ? '<span style="font-size:10px;color:var(--sub)">confidence ' + result.confidence + '/10</span>'
    : '';

  // Phase 2.5: surface how many tool calls Sonnet made + how many
  // turns the conversation took. Useful diagnostic — if a triage took
  // 9/10 iterations, it suggests Sonnet was lost or the prompt is
  // making it loop. A 1-iteration triage is suspicious for the
  // opposite reason (didn't bother to verify).
  const toolUseTag = (Array.isArray(result._toolCalls) && result._toolCalls.length > 0)
    ? '<span style="font-size:10px;color:var(--sub)">' + result._toolCalls.length + ' tool call' +
      (result._toolCalls.length === 1 ? '' : 's') +
      (typeof result._iterations === 'number' ? ' · ' + result._iterations + ' turn' + (result._iterations === 1 ? '' : 's') : '') +
      '</span>'
    : '';

  const regenerateBtn =
    '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.runTriage(\'' + source + '\',' + id + ',true)" style="font-size:11px">↻ Regenerate</button>';

  // Auto-captured "errors" rows have no human author, so the clarify
  // output is internal investigation notes rather than a customer email.
  // Relabel the button for that source so the admin isn't left wondering
  // who they'd be emailing. (The feedback source keeps "Copy email".)
  const copyLabel = (source === 'errors') ? '📋 Copy notes' : '📋 Copy email';
  const copyBtn = (result.kind === 'clarify' && result.email_draft)
    ? '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.copyTriageEmail(\'' + source + '\',' + id + ')" style="font-size:11px">' + copyLabel + '</button>'
    : '';

  // "I'll take it from here" actions for a proposed fix that wasn't
  // auto-applied. Copy-for-Claude-Code and Ask-the-store need no backend.
  const claudeCodeBtn = (result.kind === 'fix' && (result.diff || result.changes))
    ? '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.copyTriageForClaudeCode(\'' + source + '\',' + id + ')" style="font-size:11px">📋 Copy for Claude Code</button>'
    : '';
  const askStoreBtn = (result.kind === 'fix' && source === 'feedback')
    ? '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.askStoreEmail(\'' + source + '\',' + id + ')" style="font-size:11px">✉️ Ask the store</button>'
    : '';

  // Phase 2: when the worker auto-opened a draft PR, show a button that
  // jumps straight to it on GitHub. Replaces the diff display below for
  // the auto-PR case — the user reads the PR, not the inline diff.
  const viewPrBtn = (result.kind === 'fix' && result.pr_url)
    ? '<a class="btn btn-green btn-sm" href="' + escapeDevHtml(result.pr_url) + '" target="_blank" rel="noopener" style="font-size:11px;text-decoration:none">📂 View draft PR #' + escapeDevHtml(String(result.pr_number || '')) + '</a>'
    : '';

  let body =
    '<div style="font-size:13px;line-height:1.5;white-space:pre-wrap;margin-bottom:10px">' +
      escapeDevHtml(result.analysis || '—') +
    '</div>';

  if (result.kind === 'clarify' && result.email_draft) {
    const draftLabel = (source === 'errors') ? 'Investigation notes' : 'Email draft';
    body +=
      '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px">' + draftLabel + '</div>' +
      '<div style="font-size:12px;line-height:1.5;white-space:pre-wrap;background:#f9fafb;border:1px solid var(--border);border-radius:6px;padding:10px;font-family:ui-monospace,Menlo,Consolas,monospace">' +
        escapeDevHtml(result.email_draft) +
      '</div>';
  }

  if (result.kind === 'fix' && result.diff) {
    // Phase 2: when a draft PR was auto-opened, the user reads the diff
    // in GitHub (via the View PR button), so we collapse the inline
    // diff display to keep the panel compact. When confidence was too
    // low to auto-PR (or the PR open failed), the inline diff stays
    // visible so the user can apply the fix manually.
    const diffLabel = result.pr_url ? 'Diff (applied in PR — preview here)' : 'Proposed diff';
    body +=
      '<div style="font-size:10px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px">' + diffLabel + '</div>' +
      '<pre style="font-size:11px;line-height:1.45;background:#0b1020;color:#e5e7eb;border-radius:6px;padding:10px;overflow-x:auto;margin:0' + (result.pr_url ? ';max-height:160px' : '') + '">' +
        escapeDevHtml(result.diff) +
      '</pre>';
  }

  // Phase 2: surface auto-PR failures so the user knows why the diff
  // is shown without a "View PR" button. Examples: GH_PAT scope too
  // narrow, FIND text wasn't unique in the file, branch creation failed.
  if (result.kind === 'fix' && result.pr_error) {
    body +=
      '<div style="font-size:11px;color:#92400e;margin-top:8px;background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:8px">' +
        '<strong>⚠ Auto-PR not opened</strong> — ' + escapeDevHtml(result.pr_error) + '<br>' +
        '<span style="color:var(--sub)">Apply the diff manually, or check the GH_PAT has Contents:Write + Pull requests:Write on this repo.</span>' +
      '</div>';
  }

  // Ghost guard: the worker found this fix is already on staging (the
  // FIND text it targets is gone there). No PR opened — the action is to
  // promote staging to production, not to write the fix again.
  if (result.kind === 'fix' && result.already_on_staging) {
    body +=
      '<div style="font-size:11px;color:#065f46;margin-top:8px;background:#d1fae5;border:1px solid #a7f3d0;border-radius:6px;padding:8px">' +
        '<strong>✅ Looks already fixed on staging</strong> — ' +
        escapeDevHtml(result.staging_note || 'promote staging to production to ship it.') +
      '</div>';
  }

  if (Array.isArray(result.files_referenced) && result.files_referenced.length) {
    body +=
      '<div style="font-size:11px;color:var(--sub);margin-top:8px">' +
        '<strong>Files referenced:</strong> ' +
        result.files_referenced.map(p => '<code style="font-size:11px">' + escapeDevHtml(p) + '</code>').join(', ') +
      '</div>';
  }

  if (Array.isArray(result._missing) && result._missing.length) {
    // Load-bearing UX cue: when this appears, the diagnosis was made
    // without seeing some/all of the source. Common cause: GH_PAT scope
    // is wrong or a path in SCREEN_TO_FILES doesn't exist on `main`.
    body +=
      '<div style="font-size:11px;color:var(--red);margin-top:8px;background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:8px">' +
        '<strong>⚠ Source files unreachable</strong> — Claude diagnosed without seeing these:<br>' +
        result._missing.map(p => '<code style="font-size:11px">' + escapeDevHtml(p) + '</code>').join(', ') +
      '</div>';
  }

  return (
    '<div style="border-top:1px solid var(--border);margin-top:14px;padding-top:12px">' +
      '<div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;flex-wrap:wrap">' +
        pill + confidenceTag + toolUseTag + cachedTag +
        '<div style="flex:1"></div>' +
        viewPrBtn + copyBtn + claudeCodeBtn + askStoreBtn + regenerateBtn +
      '</div>' +
      body +
    '</div>'
  );
}


// ════════════════════════════════════════════════════════════
// v127.47 — 📹 JaaS check (smoke sub-tab). "8x8.vc refused to connect" in
// the call embed means 8x8 rejected the Worker-minted token and its login
// page refused framing — a kid / AppID / key-pair mismatch with the JaaS
// console. Only a human with console access can compare the strings, so
// this panel shows exactly what the Worker signs with.
// ════════════════════════════════════════════════════════════

export async function checkJaas() {
  const el = document.getElementById('jaasCheckResults');
  if (!el) return;
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:14px;text-align:center">Checking…</div>';
  const token = adminToken();
  try {
    const res = await fetch(DB.workerUrl() + '/api/calls/jaas-check', { cache: 'no-store',
      headers: { 'Authorization': 'Bearer ' + token },
    });
    if (!res.ok) {
      el.innerHTML = '<div style="color:var(--red);padding:14px">HTTP ' + res.status + ' — ' + escapeDevHtml(await res.text()) + '</div>';
      return;
    }
    const d = await res.json();
    // v127.48 — 'swapped' joined the states after the 29 Jul live catch: the
    // AppID showed a '/keyid' tail and the kid was the bare tenant (the two
    // env vars pasted into each other's box). The Worker now refuses to mint
    // in that shape (calls degrade to free meet.jit.si and keep working).
    const stateRow = d.state === 'ok'
      ? '<div style="color:#16a34a;font-weight:700">✅ Key signs — tokens are being minted</div>'
      : d.state === 'not_configured'
        ? '<div style="color:var(--sub);font-weight:700">➖ Not configured — calls run on free meet.jit.si</div>'
        : d.state === 'swapped'
          ? '<div style="color:var(--red);font-weight:700">🔁 AppID and Key ID look SWAPPED — calls are on free meet.jit.si until fixed. In the Worker env vars: <code>JAAS_APP_ID</code> must be the bare <code>vpaas-magic-cookie-…</code> tenant (no slash); <code>JAAS_KID</code> must be the FULL <code>tenant/keyname</code> string. Swap the two values — no deploy needed.</div>'
          : '<div style="color:var(--red);font-weight:700">❌ key_error — the JAAS_* vars are set but the key cannot sign. Re-paste the private key from the 8x8 console.</div>';
    let html = '<div style="padding:4px 2px">' + stateRow;
    if (d.app_id || d.kid) {
      html +=
        '<div style="font-size:12px;color:var(--sub);margin:10px 0 6px">Compare these against the JaaS console (<b>jaas.8x8.vc</b> → your app → API keys). If the embed says <b>&ldquo;8x8.vc refused to connect&rdquo;</b>, one of these does not match the console — 8x8 rejects the token and its sign-in page refuses to be embedded.</div>' +
        '<table style="font-size:12px;border-collapse:collapse">' +
        '<tr><td style="padding:3px 10px 3px 0;color:var(--sub)">AppID (sub)</td><td><code>' + escapeDevHtml(d.app_id || '') + '</code></td></tr>' +
        '<tr><td style="padding:3px 10px 3px 0;color:var(--sub)">Key ID (kid)</td><td><code>' + escapeDevHtml(d.kid || '') + '</code></td></tr>' +
        '</table>' +
        '<div style="font-size:12px;color:var(--sub);margin-top:8px">The AppID never contains a slash; the kid must be the console&rsquo;s FULL key id (usually <code>vpaas-magic-cookie-…/keyname</code>), and the private key must be the pair of the public key shown on that exact row. Wrong app, a regenerated key, a truncated kid or swapped values all land on &ldquo;refused to connect&rdquo;.</div>';
    }
    html += '</div>';
    el.innerHTML = html;
  } catch (e) {
    el.innerHTML = '<div style="color:var(--red);padding:14px">Could not reach the Worker — ' + escapeDevHtml((e && e.message) || String(e)) + '</div>';
  }
}

// ════════════════════════════════════════════════════════════
// v128.0 — 📖 policy mirror sync (smoke sub-tab). Pulls the JACK-memory
// policy library into the `policies` mirror table (docs/policies-dg-plan.md).
// env-gated on JACK_SUPABASE_URL/KEY: absent = not configured, nothing runs.
// ════════════════════════════════════════════════════════════

export async function syncPolicies() {
  const el = document.getElementById('policySyncResults');
  if (!el) return;
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:14px;text-align:center">Syncing…</div>';
  const token = adminToken();
  try {
    const res = await fetch(DB.workerUrl() + '/admin/policies/sync', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.ok === false) {
      el.innerHTML = '<div style="color:var(--red);padding:14px">' + escapeDevHtml((d && d.error) || ('HTTP ' + res.status)) + '</div>';
      return;
    }
    if (d.state === 'not_configured') {
      el.innerHTML = '<div style="color:var(--sub);padding:14px;font-weight:700">➖ Not configured — set JACK_SUPABASE_URL + JACK_SUPABASE_KEY on this Worker (a read-only key for the JACK project), then sync. Nothing runs until then.</div>';
      return;
    }
    // v128.1 — a ZERO-count "success" is almost never success: a valid but
    // RLS-blocked key reads 200-[] (the publishable-key lesson, live 29 Jul).
    // Say so instead of a cheerful tick.
    if (!Number(d.synced || 0)) {
      el.innerHTML = '<div style="color:#b45309;padding:14px;font-weight:700">⚠ The pipe works but 0 policies came back — if JACK has policies, this key cannot read them (RLS filters anonymous reads to empty). Swap JACK_SUPABASE_KEY to the JACK project&rsquo;s SECRET key and sync again.</div>';
      return;
    }
    el.innerHTML = '<div style="color:#16a34a;padding:14px;font-weight:700">✅ Synced ' + Number(d.synced) + ' policies' +
      (d.deactivated ? ' · ' + Number(d.deactivated) + ' deactivated (removed in JACK)' : '') +
      '</div><div style="font-size:12px;color:var(--sub);padding:0 14px 10px">The in-app reader is next — this button proves the pipe. The nightly cron keeps the mirror fresh automatically.</div>';
  } catch (e) {
    el.innerHTML = '<div style="color:var(--red);padding:14px">Could not reach the Worker — ' + escapeDevHtml((e && e.message) || String(e)) + '</div>';
  }
}

// ── 🗄 Data retention panel (v128.42 — Matt's 31 Jul ruling) ─────────────────
// Loads /api/admin/retention-config into #retentionResults: the fixed ops
// windows (read-only), the product windows (editable) and THE toggle. Saving
// with the toggle ON is the go-live moment for scheduled deletion — the
// button demands a typed confirm, and the panel nags about the privacy
// notice (the activation checklist in FOLLOWUPS.md).
export async function retentionPanel() {
  const el = document.getElementById('retentionResults');
  if (!el) return;
  el.innerHTML = '<div style="padding:14px;color:var(--sub)">Loading…</div>';
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/retention-config', {
      cache: 'no-store', headers: { 'Authorization': 'Bearer ' + (adminToken()) },
    });
    if (!res.ok) { el.innerHTML = '<div style="color:var(--red);padding:14px">Failed to load (' + res.status + ').</div>'; return; }
    const d = await res.json();
    const p = d.product || {};
    const opsRows = (d.ops || []).map(function (r) {
      return '<tr><td style="padding:4px 10px 4px 0">' + r.table + '</td><td style="padding:4px 0;color:var(--sub)">' + r.days + ' days (fixed)</td></tr>';
    }).join('');
    el.innerHTML =
      '<div style="padding:6px 0 12px;font-size:13px">' +
      '<div style="font-weight:800;margin-bottom:4px">Product data (Matt&rsquo;s ruling — editable)</div>' +
      '<div style="display:flex;gap:16px;flex-wrap:wrap;align-items:center;margin-bottom:8px">' +
      '<label>Chats + store emails: <input id="retChatDays" type="number" min="7" max="365" value="' + Number(p.chatEmailDays || 31) + '" style="width:70px"> days</label>' +
      '<label>Task/news media + comments: <input id="retContentDays" type="number" min="30" max="3650" value="' + Number(p.contentDays || 183) + '" style="width:70px"> days</label>' +
      '<label style="font-weight:800"><input id="retEnabled" type="checkbox"' + (p.enabled ? ' checked' : '') + '> Enable scheduled deletion</label>' +
      '<button class="btn btn-sm" onclick="Uploads.devReports.retentionSave()">Save</button>' +
      '</div>' +
      '<div style="font-size:12px;color:var(--sub);margin-bottom:10px">Top-level records (tasks, completions, news posts, confirms) are kept forever — only media, comments, chats and emails expire. Status: <strong>' + (p.enabled ? 'LIVE — deleting nightly' : 'DARK — nothing deletes yet') + '</strong>.</div>' +
      '<div style="font-weight:800;margin-bottom:4px">Ops data (fixed windows, live since v128.17)</div>' +
      '<table style="font-size:12px">' + opsRows + '</table>' +
      '<div style="font-size:12px;color:var(--sub);margin-top:8px">Pay/staff records: kept for employment + legal retention (separate 365d ops purge untouched). Never touched here: badge_awards, jack_findings, write_audit_log.</div>' +
      '</div>';
  } catch (e) {
    el.innerHTML = '<div style="color:var(--red);padding:14px">Failed: ' + escapeDevHtml((e && e.message) || String(e)) + '</div>';
  }
}

// ── 🔐 Customer data panel (v131.45, post-Cubes sweep F2) ─────────────────────
// Config + live status from /api/admin/customer-privacy/config; export and
// the emergency purge are two deliberate clicks, both audited server-side.
function cpFmt(iso) { try { return iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'; } catch (e) { return String(iso || '-'); } }
export async function customerPrivacyPanel() {
  const el = document.getElementById('customerPrivacyResults');
  if (!el) return;
  el.innerHTML = '<div style="padding:14px;color:var(--sub)">Loading…</div>';
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/customer-privacy/config', { cache: 'no-store', headers: { 'Authorization': 'Bearer ' + (adminToken()) } });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok) { el.innerHTML = '<div style="color:var(--red);padding:14px">' + escapeDevHtml(d.error || ('Failed to load (' + res.status + ')')) + '</div>'; return; }
    const c = d.config || {};
    const s = d.status || {};
    el.innerHTML =
      '<div style="padding:6px 0 12px;font-size:13px">' +
      '<div style="margin-bottom:8px">Holding contact details right now: <strong>' + (s.feedback_rows_with_contact == null ? '?' : s.feedback_rows_with_contact) + '</strong> reviews (oldest ' + escapeDevHtml(cpFmt(s.oldest_feedback_contact)) + ').</div>' +
      '<div style="display:flex;gap:16px;flex-wrap:wrap;align-items:center;margin-bottom:8px">' +
      '<label>Review contact details kept: <input id="cpFeedbackDays" type="number" min="30" max="3650" value="' + Number(c.feedbackContactDays || 90) + '" style="width:70px"> days</label>' +
      '<label style="font-weight:800"><input id="cpEnabled" type="checkbox"' + (c.enabled !== false ? ' checked' : '') + '> Null them nightly</label>' +
      '<button class="btn btn-sm" onclick="Uploads.devReports.customerPrivacySave()">Save</button>' +
      '</div>' +
      '<div style="font-size:12px;color:var(--sub);margin-bottom:12px">Status: <strong>' + (c.enabled !== false ? 'LIVE - nulling nightly' : 'OFF - nothing is nulled') + '</strong>. Ratings, comments, stores and the opt-in flag are never touched; only who the person is.</div>' +
      '<div style="border-top:1px solid var(--border);padding-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">' +
      '<span style="font-weight:800">If we are under attack:</span>' +
      '<button class="btn btn-outline btn-sm" onclick="Uploads.devReports.customerPrivacyExport()">1. Export contact details (JSON)</button>' +
      '<button class="btn btn-outline btn-sm" style="color:var(--red);border-color:var(--red)" onclick="Uploads.devReports.customerPrivacyPurgeAll()">2. Purge ALL contact details now</button>' +
      '<span style="font-size:12px;color:var(--sub)">Both are audited. Keep the export somewhere safe and off ALGT mail.</span>' +
      '</div></div>';
  } catch (e) {
    el.innerHTML = '<div style="color:var(--red);padding:14px">Failed: ' + escapeDevHtml((e && e.message) || String(e)) + '</div>';
  }
}
export async function customerPrivacySave() {
  const body = {
    enabled: !!(document.getElementById('cpEnabled') || {}).checked,
    feedbackContactDays: Number((document.getElementById('cpFeedbackDays') || {}).value),
  };
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/customer-privacy/config', { method: 'POST', cache: 'no-store', headers: { 'Authorization': 'Bearer ' + (adminToken()), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok) { showToast(d.error || ('Save failed (' + res.status + ')'), 'error'); return; }
    showToast('Customer data settings saved', 'success');
    customerPrivacyPanel();
  } catch (e) { showToast('Save failed: ' + ((e && e.message) || e), 'error'); }
}
export async function customerPrivacyExport() {
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/customer-privacy/export', { cache: 'no-store', headers: { 'Authorization': 'Bearer ' + (adminToken()) } });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok) { showToast(d.error || ('Export failed (' + res.status + ')'), 'error'); return; }
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'shift-customer-contacts-' + new Date().toISOString().slice(0, 10) + '.json';
    document.body.appendChild(a); a.click(); a.remove();
    showToast('Exported ' + (d.feedback_contacts || []).length + ' reviews', 'success');
  } catch (e) { showToast('Export failed: ' + ((e && e.message) || e), 'error'); }
}
export async function customerPrivacyPurgeAll() {
  const typed = prompt('This nulls EVERY customer contact detail (names, phones, emails, IPs) on every review, regardless of age. Ratings and comments stay. It cannot be undone - export first.\n\nType PURGE to continue.');
  if (typed !== 'PURGE') return;
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/customer-privacy/purge-all', { method: 'POST', cache: 'no-store', headers: { 'Authorization': 'Bearer ' + (adminToken()), 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm: 'PURGE' }) });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok) { showToast(d.error || ('Purge failed (' + res.status + ')'), 'error'); return; }
    showToast('Purged: ' + d.customer_feedback + ' reviews' + (d.errors && d.errors.length ? ' (with errors)' : ''), d.ok ? 'success' : 'error');
    customerPrivacyPanel();
  } catch (e) { showToast('Purge failed: ' + ((e && e.message) || e), 'error'); }
}

// ── 🧠 DB usage panel (v128.44 — the per-table dive) ─────────────────────────
// Loads /api/admin/db-usage into #dbUsageResults. The Worker calls the
// db_usage_report() RPC (migration v178); a 501 here means the RPC is not
// installed yet — the message names the file to run.
export async function dbUsagePanel() {
  const el = document.getElementById('dbUsageResults');
  if (!el) return;
  el.innerHTML = '<div style="padding:14px;color:var(--sub)">Loading…</div>';
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/db-usage', {
      cache: 'no-store', headers: { 'Authorization': 'Bearer ' + (adminToken()) },
    });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok) {
      el.innerHTML = '<div style="color:var(--red);padding:14px">' + escapeDevHtml(d.error || ('Failed to load (' + res.status + ')')) + '</div>';
      return;
    }
    const conns = d.connections || {};
    const connLine = Object.keys(conns).map(function (k) { return conns[k] + ' ' + k; }).join(', ') || 'none reported';
    const rows = (d.top_tables || []).map(function (t) {
      return '<tr><td style="padding:3px 10px 3px 0">' + escapeDevHtml(t.table) + '</td>' +
        '<td style="padding:3px 10px;text-align:right;font-weight:700">' + Number(t.total_mb) + '</td>' +
        '<td style="padding:3px 10px;text-align:right;color:var(--sub)">' + Number(t.table_mb) + '</td>' +
        '<td style="padding:3px 10px;text-align:right;color:var(--sub)">' + Number(t.index_mb) + '</td>' +
        '<td style="padding:3px 10px;text-align:right;color:var(--sub)">' + Number(t.toast_mb) + '</td>' +
        '<td style="padding:3px 0;text-align:right;color:var(--sub)">' + Number(t.approx_rows).toLocaleString() + '</td></tr>';
    }).join('');
    el.innerHTML =
      '<div style="padding:6px 0 12px;font-size:13px">' +
      '<div style="margin-bottom:8px">Database <strong>' + Number(d.db_size_mb) + 'MB</strong>' +
      ' · connections: ' + escapeDevHtml(connLine) + (d.max_connections ? ' (max ' + Number(d.max_connections) + ')' : '') +
      ' · cache hit <strong>' + Number(d.cache_hit_pct) + '%</strong>' +
      (d.temp ? ' · temp spill ' + Number(d.temp.mb) + 'MB / ' + Number(d.temp.files) + ' files (since last restart)' : '') +
      '</div>' +
      '<table style="font-size:12px"><tr style="font-weight:800"><td style="padding:3px 10px 3px 0">Table</td>' +
      '<td style="padding:3px 10px;text-align:right">Total MB</td><td style="padding:3px 10px;text-align:right">Rows MB</td>' +
      '<td style="padding:3px 10px;text-align:right">Index MB</td><td style="padding:3px 10px;text-align:right">Media MB</td>' +
      '<td style="padding:3px 0;text-align:right">~Rows</td></tr>' + rows + '</table>' +
      '<div style="font-size:12px;color:var(--sub);margin-top:8px">Media MB = TOAST (large values stored out-of-line — photos/attachments in rows). Cache hit under ~95% or growing temp spill = the working set no longer fits in RAM.</div>' +
      '</div>';
  } catch (e) {
    el.innerHTML = '<div style="color:var(--red);padding:14px">Failed: ' + escapeDevHtml((e && e.message) || String(e)) + '</div>';
  }
}

export async function retentionSave() {
  const el = document.getElementById('retentionResults');
  const enabled = !!(document.getElementById('retEnabled') || {}).checked;
  const chatEmailDays = Number((document.getElementById('retChatDays') || {}).value) || 31;
  const contentDays = Number((document.getElementById('retContentDays') || {}).value) || 183;
  if (enabled) {
    // The go-live gate: deleting a month of chat across 28 stores is not a
    // misclick-grade action. A typed confirm, always.
    const typed = prompt('This ENABLES nightly deletion (chats/emails older than ' + chatEmailDays +
      ' days, task/news media older than ' + contentDays + ' days). Type DELETE to confirm — and update the privacy notice in the same release.');
    if (typed !== 'DELETE') { if (typeof showToast === 'function') showToast('Not enabled — confirmation not typed', 'info'); return; }
  }
  try {
    const res = await fetch(DB.workerUrl() + '/api/admin/retention-config', {
      method: 'POST', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (adminToken()) },
      body: JSON.stringify({ enabled: enabled, chatEmailDays: chatEmailDays, contentDays: contentDays }),
    });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok) { if (typeof showToast === 'function') showToast('Save failed: ' + (d.error || res.status), 'error'); return; }
    if (typeof showToast === 'function') showToast(enabled ? '⚠ Scheduled deletion is LIVE from tonight' : 'Saved (still dark — nothing deletes)', enabled ? 'info' : 'success');
    retentionPanel();
  } catch (e) {
    if (typeof showToast === 'function') showToast('Save failed: ' + ((e && e.message) || String(e)), 'error');
  }
}
